import React from 'react';
import { SyncAlt } from '@mui/icons-material';
import { generatePath, Link } from 'react-router-dom';
import { debounceSearchRender } from 'mui-datatables';
import moment from 'moment';
import superSearch from '@codewell/super-search';
import routes from '../routes';
import { getUserFullName } from '../helpers/user';
import { isUndefinedOrNull } from '../helpers';
import * as usersModel from '../models/users';
import * as authService from '../services/auth';
import { formatUserRole, formatUserRoles } from '../helpers/formatters';
import BaseMUIDataTable from './BaseMUIDataTable/BaseMUIDataTable';
import { userPhoneVerifiedFilterOptions, userRegistrationFilterOptions } from '../helpers/MuiTableCustomFilters';
import TableActionsButtonsWrapper from './TableActionsButtonsWrapper';
import TableActionButton from './TableActionButton';
import OverlayLoader from './OverlayLoader';

const UsersTableNew = ({ onRowClick, data = [], isLoading = false }) => {
  const fullNameCustomBodyRender = (dataIndex) => {
    const foundUser = data[dataIndex];

    if (!foundUser) {
      return '';
    }

    return getUserFullName(foundUser);
  };

  const emailVerifiedCustomBodyRender = (dataIndex) => {
    const foundUser = data[dataIndex];
    const emailVerified = foundUser?.email_verified;

    return !isUndefinedOrNull(emailVerified)
      ? moment(emailVerified, moment.ISO_8601).format('MM/DD/YYYY')
      : 'Not Verified';
  };

  const phoneCustomBodyRender = (dataIndex) => {
    const user = data[dataIndex];
    if (!user?.active) {
      return 'Deactivated';
    }

    const phone = user?.phone;
    return !isUndefinedOrNull(phone) ? phone.replace(/^([0-9]{3})([0-9]{3})([0-9]{4})$/, '$1-$2-$3') : null;
  };

  const phoneVerifiedCustomBodyRender = (dataIndex) => {
    const user = data[dataIndex];
    const phoneVerified = user?.phone_verified;
    return !isUndefinedOrNull(phoneVerified)
      ? moment(phoneVerified, moment.ISO_8601).format('MM/DD/YYYY')
      : 'Not Verified';
  };

  const activeCustomBodyRender = (dataIndex) => {
    const user = data[dataIndex];
    const active = user?.active;
    return active === 1 ? 'yes' : 'no';
  };

  const completedRegistrationCustomBodyRender = (dataIndex) => {
    const user = data[dataIndex];
    const completedRegistration = user?.completed_registration;
    return !isUndefinedOrNull(completedRegistration)
      ? moment(completedRegistration, 'YYYY-MM-DD').format('MM/DD/YYYY')
      : null;
  };

  const getFormattedUserRoles = (roleAssignments) => {
    const userRoles = usersModel.getRolesFromRoleAssignments(
      authService.getUserRolesInCurrentlySelectedSite({ role_assignments: roleAssignments })
    );

    if (!userRoles || !userRoles.length) {
      return '-';
    }

    const formattedUserRoles = formatUserRoles(userRoles).join(', ');

    return formattedUserRoles;
  };

  const roleAssignmentsCustomBodyRender = (dataIndex) => getFormattedUserRoles(data[dataIndex]?.role_assignments);

  const actionsCustomBodyRender = (dataIndex) => {
    const user = data[dataIndex];

    return (
      <TableActionsButtonsWrapper>
        <TableActionButton
          title="Edit User"
          buttonProps={{
            component: Link,
            to: generatePath(routes.userEdit, { id: user.id }),
          }}
        />

        <TableActionButton title="Impersonate User" icon={SyncAlt} onClick={() => onRowClick(user.id)} />
      </TableActionsButtonsWrapper>
    );
  };

  return (
    <OverlayLoader isLoading={isLoading}>
      <BaseMUIDataTable
        tableId="#switch-user"
        title="Users"
        data={data}
        columns={[
          {
            name: 'id',
            label: 'Id',
            options: {
              filter: false,
              sort: false,
              display: false,
            },
          },
          {
            name: 'firstname',
            label: 'First Name',
            options: {
              filter: false,
              display: false,
            },
          },
          {
            name: 'lastname',
            label: 'Last Name',
            options: {
              filter: false,
              display: false,
            },
          },
          {
            name: 'fullname',
            label: 'Full Name',
            options: {
              filter: false,
              customBodyRenderLite: (dataIndex) => fullNameCustomBodyRender(dataIndex),
            },
          },
          {
            name: 'email',
            label: 'Email',
            options: {
              filter: false,
            },
          },
          {
            name: 'email_verified',
            label: 'Email Verified',
            options: {
              filter: false,
              display: false,
              customBodyRenderLite: (dataIndex) => emailVerifiedCustomBodyRender(dataIndex),
              customFilterListOptions: {
                render: (value) => `Email Verified: ${value}`,
              },
            },
          },
          {
            name: 'phone',
            label: 'Phone',
            options: {
              filter: false,
              customBodyRenderLite: (dataIndex) => phoneCustomBodyRender(dataIndex),
            },
          },
          {
            name: 'phone_verified',
            label: 'Phone Verified',
            options: {
              filter: false,
              display: false,
              ...userPhoneVerifiedFilterOptions,
              customBodyRenderLite: (dataIndex) => phoneVerifiedCustomBodyRender(dataIndex),
            },
          },
          {
            name: 'active',
            label: 'Active',
            options: {
              filter: false,
              customBodyRenderLite: (dataIndex) => activeCustomBodyRender(dataIndex),
            },
          },
          {
            name: 'role_assignments',
            label: 'Roles',
            options: {
              filterType: 'custom',
              sortCompare:
                (order) =>
                ({ data: roles1 }, { data: roles2 }) => {
                  const userRoles1 = usersModel.getRolesFromRoleAssignments(
                    authService.getUserRolesInCurrentlySelectedSite({ role_assignments: formatUserRoles(roles1) })
                  );
                  const userRoles2 = usersModel.getRolesFromRoleAssignments(
                    authService.getUserRolesInCurrentlySelectedSite({ role_assignments: formatUserRoles(roles2) })
                  );

                  if (order === 'asc') {
                    return usersModel.rolesWeights[userRoles2[0]] - usersModel.rolesWeights[userRoles1[0]];
                  }

                  return usersModel.rolesWeights[userRoles1[0]] - usersModel.rolesWeights[userRoles2[0]];
                },
              customFilterListOptions: {
                render: (value) => `Role: ${value.join(', ')}`,
              },
              filterOptions: {
                logic: (roleAssignments, filters) => {
                  if (!filters || filters.length === 0) {
                    return false;
                  }

                  return !filters.every((filter) =>
                    Object.values(roleAssignments).find((role) => formatUserRole(role.role_name) === filter)
                  );
                },
              },
              customBodyRenderLite: (dataIndex) => roleAssignmentsCustomBodyRender(dataIndex),
            },
          },
          {
            name: 'completed_registration',
            label: 'Completed Registration',
            options: {
              filter: true,
              display: false,
              customBodyRenderLite: (dataIndex) => completedRegistrationCustomBodyRender(dataIndex),
              ...userRegistrationFilterOptions,
            },
          },
          {
            name: 'actions',
            label: 'Actions',
            options: {
              download: false,
              filter: false,
              sort: false,
              customBodyRenderLite: (dataIndex) => actionsCustomBodyRender(dataIndex),
            },
          },
        ]}
        options={{
          customSearchRender: debounceSearchRender(500),
          customSearch: (searchQuery, currentRow, columns) => {
            const firstNameIndex = columns.findIndex(({ name }) => name === 'firstname');
            const lastNameIndex = columns.findIndex(({ name }) => name === 'lastname');
            const rolesIndex = columns.findIndex(({ name }) => name === 'role_assignments');

            // search roles by their displayed value instead of the raw role assignments
            const searchableRow = currentRow.map((cell, index) =>
              index === rolesIndex ? getFormattedUserRoles(cell) : cell
            );

            const { numberOfMatches, fullMatch } = superSearch(searchQuery.toLowerCase(), {
              ...[`${currentRow[firstNameIndex]} ${currentRow[lastNameIndex]}`, ...searchableRow],
            });

            return fullMatch && numberOfMatches > 0;
          },
          responsive: 'simple',
          selectableRows: 'none',
          print: false,
          download: false,
          rowsPerPage: 15,
        }}
      />
    </OverlayLoader>
  );
};

export default UsersTableNew;
